import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { readApiOutcome } from '../../../utils/apiOutcome';

// Holds one pending archive decision (see archiveActions) behind a ConfirmDialog: the view asks
// for confirmation, and only a confirmed, successful call runs its `onSucceeded` refresh.
// `dialogProps` spreads straight onto ConfirmDialog.
//
// The first request wins: while a confirmation is open, later requests are refused rather than
// swapping the dialog's target, so Confirm always acts on what the user is looking at. A ref
// mirrors the open state so two requests in the same tick can't both get through.
export function useArchiveConfirmation() {
  const [pending, setPendingState] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const isOpen = useRef(false);

  const setPending = (next) => {
    isOpen.current = next !== null;
    setPendingState(next);
  };

  const requestConfirmation = (action, onSucceeded = () => {}) => {
    if (isOpen.current) return false;
    setPending({ action, onSucceeded });
    return true;
  };

  const dismiss = () => {
    if (inProgress) return;
    setPending(null);
  };

  const confirm = async () => {
    if (!pending || inProgress) return;
    const { action, onSucceeded } = pending;

    setInProgress(true);
    let outcome;
    try {
      outcome = readApiOutcome(await action.perform());
    } catch {
      outcome = { succeeded: false, message: '' };
    }
    setInProgress(false);
    setPending(null);

    if (!outcome.succeeded) {
      toast.error(outcome.message || action.failureMessage);
      return;
    }
    toast.success(action.successMessage);
    onSucceeded();
  };

  const action = pending?.action;
  return {
    requestConfirmation,
    dialogProps: {
      open: Boolean(pending),
      onClose: dismiss,
      onConfirm: confirm,
      title: action?.title,
      message: action?.message,
      confirmLabel: action?.confirmLabel,
      destructive: Boolean(action?.destructive),
      loading: inProgress,
    },
  };
}
